import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import {
  type ApiError,
  type ApiResponse,
  addActionNameToMutationResult,
  useAxiosInstance,
} from 'shared/hooks/utils';
import { z } from 'zod/v4';

const URL = '/payment_plan_settings';

const UpdatePaymentPlanSettingsVariablesSchema = z.object({
  allowPaymentPlans: z.boolean(),
});

export type UpdatePaymentPlanSettingsVariables = z.infer<
  typeof UpdatePaymentPlanSettingsVariablesSchema
>;

export async function updatePaymentPlanSettingsFn(
  axiosInstance: Promise<AxiosInstance>,
  variables: UpdatePaymentPlanSettingsVariables,
) {
  const instance = await axiosInstance;

  return instance.put(URL, UpdatePaymentPlanSettingsVariablesSchema.parse(variables));
}

export function useUpdatePaymentPlanSettings() {
  const client = useQueryClient();
  const axiosInstance = useAxiosInstance();

  const mutation = useMutation<ApiResponse, ApiError, UpdatePaymentPlanSettingsVariables>({
    mutationFn: variables => updatePaymentPlanSettingsFn(axiosInstance, variables),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['paymentPlanSettings'] });
      client.invalidateQueries({ queryKey: ['companyConfigurationV2'] });
    },
  });

  return addActionNameToMutationResult<
    ApiResponse,
    ApiError,
    UpdatePaymentPlanSettingsVariables,
    'updatePaymentPlanSettings'
  >('updatePaymentPlanSettings', mutation);
}
